import type { ReactElement } from "react";
import { Award, Building2, Cake, Gift, PartyPopper, Star, Trophy, type LucideIcon } from "lucide-react";

/**
 * One inline icon per built-in festival icon key (24x24). The fi-* classes animate their parts;
 * the keyframes live in tokens.css. Company events pick from COMPANY_ICONS instead.
 */
export const FESTIVE_ICONS: Record<string, ReactElement> = {
  diwali: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle className="fi-glowc" cx="12" cy="8.2" r="7" fill="#FFB347" opacity="0.28" />
      <path d="M2.5 13.5c0 4.2 4.2 7 9.5 7s9.5-2.8 9.5-7z" fill="#E08A3C" />
      <path d="M2.5 13.5h19" stroke="#FFD9A0" strokeWidth="1.3" />
      <path d="M5.2 17.2c1.6 1.8 4 2.8 6.8 2.8s5.2-1 6.8-2.8" fill="none" stroke="#9A4A08" strokeWidth="1.2" opacity="0.6" />
      <g className="fi-flame">
        <path d="M12 2.5c2.3 2.8 3.4 4.6 3.4 6.4a3.4 3.4 0 01-6.8 0c0-1.8 1.1-3.6 3.4-6.4z" fill="#FFC857" />
        <path d="M12 6.5c1 1.3 1.5 2.2 1.5 3a1.5 1.5 0 01-3 0c0-.8.5-1.7 1.5-3z" fill="#FF7A18" />
      </g>
    </svg>
  ),
  xmas: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3.5l5 6h-2.5l4 5h-3.5l4 5h-14l4-5H6.5l4-5H8z" fill="#2FB36B" />
      <rect x="10.7" y="19" width="2.6" height="2.5" fill="#8A5A2B" />
      <path className="fi-star" d="M12 .8l1.1 2.2 2.4.3-1.8 1.7.5 2.4-2.2-1.2-2.2 1.2.5-2.4-1.8-1.7 2.4-.3z" fill="#FFD166" />
      <circle className="fi-b1" cx="10" cy="12.5" r="1.1" fill="#FF4D4D" />
      <circle className="fi-b2" cx="14.2" cy="15" r="1.1" fill="#60A5FA" />
      <circle className="fi-b3" cx="9" cy="17.4" r="1.1" fill="#FFD166" />
    </svg>
  ),
  newyear: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path className="fi-sp" d="M11 3l1.9 6.1L19 11l-6.1 1.9L11 19l-1.9-6.1L3 11l6.1-1.9z" fill="#FFD166" />
      <path className="fi-sp s2" d="M19 14l.9 2.9 2.9.9-2.9.9L19 21.6l-.9-2.9-2.9-.9 2.9-.9z" fill="#FFFFFF" />
      <path className="fi-sp s3" d="M19.5 2.5l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6z" fill="#60A5FA" />
    </svg>
  ),
  holi: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle className="fi-splash" cx="9" cy="10" r="6" fill="#FF3D81" opacity="0.92" />
      <circle className="fi-splash s2" cx="15.5" cy="9" r="5.2" fill="#FFD166" opacity="0.92" />
      <circle className="fi-splash s3" cx="12.5" cy="15.2" r="5.4" fill="#22D3EE" opacity="0.88" />
      <circle cx="4" cy="4.5" r="1.3" fill="#4ADE80" />
      <circle cx="20.5" cy="4" r="1.1" fill="#FF8A3D" />
      <circle cx="20" cy="19.5" r="1.4" fill="#A78BFA" />
    </svg>
  ),
  eid: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path className="fi-cres" d="M15.2 3.2a9 9 0 1 0 5.6 13.4 7.3 7.3 0 0 1-5.6-13.4z" fill="#FFD166" />
      <path className="fi-star" d="M18 4.6l.9 1.9 2.1.3-1.5 1.5.4 2.1-1.9-1-1.9 1 .4-2.1-1.5-1.5 2.1-.3z" fill="#FFFFFF" />
    </svg>
  ),
  flag: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3v18" stroke="#B7C2D5" strokeWidth="1.6" strokeLinecap="round" />
      <g className="fi-wave">
        <rect x="5" y="4" width="15.5" height="3.6" fill="#FF9933" />
        <rect x="5" y="7.6" width="15.5" height="3.6" fill="#F4F6FA" />
        <rect x="5" y="11.2" width="15.5" height="3.6" fill="#138808" />
        <circle cx="12.75" cy="9.4" r="1.35" fill="none" stroke="#1E3A8A" strokeWidth="0.7" />
      </g>
    </svg>
  ),
};

/** Company event icons, drawn as line icons in the badge and tinted by the event's colour. */
const COMPANY_ICONS: Record<string, LucideIcon> = {
  trophy: Trophy,
  cake: Cake,
  party: PartyPopper,
  gift: Gift,
  award: Award,
  star: Star,
  building: Building2,
};

export const isCompanyIcon = (key: string): boolean => Object.prototype.hasOwnProperty.call(COMPANY_ICONS, key);

/** The icon to draw for a key, or null when the key is unknown. */
export function festiveIcon(key: string): ReactElement | null {
  const Company = COMPANY_ICONS[key];
  if (Company) return <Company aria-hidden="true" strokeWidth={1.8} />;
  return FESTIVE_ICONS[key] ?? null;
}
